import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Globe, ArrowLeft, Clock } from 'lucide-react';
import { programAPI } from '../../services/programApi';
import { toast } from '../../hooks/use-toast';

const DayTest = () => {
  const navigate = useNavigate();
  const { language, dayNumber } = useParams();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Create a free account to take tests and track progress.',
        variant: 'destructive',
      });
      navigate('/register');
      return;
    }
    generateTest();
  }, [language, dayNumber]);

  const generateTest = async () => {
    try {
      const data = await programAPI.generateTest(language, parseInt(dayNumber), false);
      setQuestions(data.questions);
    } catch (error) {
      console.error('Error generating test:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate test',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionIndex, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    if (Object.keys(answers).length < questions.length) {
      toast({
        title: 'Incomplete Test',
        description: 'Please answer all questions',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const answerArray = questions.map((_, index) => answers[index] || 0);
      const data = await programAPI.submitTest(language, answerArray, parseInt(dayNumber), false);
      setResults(data);
      setShowResults(true);

      if (data.passed) {
        toast({
          title: 'Congratulations!',
          description: `You passed with ${data.percentage.toFixed(1)}%`,
        });
      } else {
        toast({
          title: 'Keep Trying',
          description: `You scored ${data.percentage.toFixed(1)}%. Need 70% to pass.`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit test',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Generating your test...</p>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
        <nav className="border-b bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">Test Results</span>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className={`border-2 mb-8 ${
            results.passed ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
          }`}>
            <CardContent className="p-8 text-center">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                {results.passed ? '🎉 Congratulations!' : '📚 Keep Practicing'}
              </h2>
              <p className="text-6xl font-bold text-amber-600 mb-4">
                {results.score}/{results.total}
              </p>
              <p className="text-2xl text-slate-700 mb-2">
                {results.percentage.toFixed(1)}%
              </p>
              <p className="text-slate-600">
                {results.passed 
                  ? 'You passed! The next day is now unlocked.' 
                  : 'You need 70% to pass. Review the lesson and try again.'}
              </p>
            </CardContent>
          </Card>

          {/* Answer Review */}
          <div className="space-y-4 mb-8">
            {questions.map((question, index) => {
              const userAnswer = answers[index];
              const correctAnswer = results.correct_answers[index];
              const isCorrect = userAnswer === correctAnswer;

              return (
                <Card key={index} className={`border-2 ${
                  isCorrect ? 'border-emerald-300 bg-emerald-50/50' : 'border-rose-300 bg-rose-50/50'
                }`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <span className={`text-2xl ${
                        isCorrect ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {isCorrect ? '✓' : '✗'}
                      </span>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 mb-3">
                          {index + 1}. {question.question}
                        </p>
                        <div className="space-y-2">
                          {question.options.map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className={`p-3 rounded-lg ${
                                optIndex === correctAnswer
                                  ? 'bg-emerald-100 border-2 border-emerald-400'
                                  : optIndex === userAnswer && !isCorrect
                                  ? 'bg-rose-100 border-2 border-rose-400'
                                  : 'bg-white border'
                              }`}
                            >
                              <p className="text-slate-900">{option}</p>
                            </div>
                          ))}
                        </div>
                        {results.explanations[index] && (
                          <div className="mt-3 p-3 bg-sky-50 rounded-lg border">
                            <p className="text-sm text-slate-700">
                              <strong>Explanation:</strong> {results.explanations[index]}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => navigate(`/program/${language}/day/${dayNumber}`)}
            >
              Review Lesson
            </Button>
            {results.passed ? (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate(`/program/${language}`)}
              >
                Continue to Next Day
              </Button>
            ) : (
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => {
                  setShowResults(false);
                  setAnswers({});
                  generateTest();
                }}
              >
                Retake Test
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/program/${language}/day/${dayNumber}`)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">
                  Day {dayNumber} Test
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Test Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Day {dayNumber} Test</h1>
          <div className="flex items-center gap-4 text-slate-600">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              No time limit
            </span>
            <span>10 questions</span>
            <span>70% to pass</span>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-6 mb-8">
          {questions.map((question, index) => (
            <Card key={index} className="border-2">
              <CardHeader>
                <CardTitle className="text-lg">
                  Question {index + 1} of {questions.length}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-900 font-medium mb-4">{question.question}</p>
                <RadioGroup
                  value={answers[index]?.toString()}
                  onValueChange={(value) => handleAnswerChange(index, parseInt(value))}
                >
                  {question.options.map((option, optIndex) => (
                    <div key={optIndex} className="flex items-center space-x-2 p-3 rounded-lg hover:bg-slate-50">
                      <RadioGroupItem value={optIndex.toString()} id={`q${index}-opt${optIndex}`} />
                      <Label htmlFor={`q${index}-opt${optIndex}`} className="flex-1 cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 px-12"
            onClick={handleSubmit}
            disabled={submitting || Object.keys(answers).length < questions.length}
          >
            {submitting ? 'Submitting...' : 'Submit Test'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DayTest;